import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";

import AppT9N from "../../../t9n/App/App.json";
import "./MyAppsSidebar.scss";
import NavBarViewModel, {
  HIGH_VAL_UNICODE_CHAR,
} from "../../ui/NavBar/NavBarViewModel";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import { VNode } from "../../../interfaces/interfaces";
import { getLocale } from "@arcgis/core/intl";
import { findAppTypeBadge } from "../../utils/appUtils";

@subclass("app.widgets.Sidebar.MyAppsSidebar")
export default class MyAppsSidebar extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  navbarVM: NavBarViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  private _locale;

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  constructor(args) {
    super(args);
  }

  postInitialize(): void {
    this._locale = getLocale();
  }

  render() {
    const {
      choose,
      chooseInfo,
      copy,
      copyInfo,
      configure,
      configureInfo,
      ownerHeader,
      myApps,
      myOrgApps,
      templateHeader,
      shareHeader,
      owner,
      organization,
      publicLabel,
    } = this.appMessages.myAppsTabSidebar;

    const { isPublicAcct } = this.navbarVM.myAppsDataVM;

    return (
      <div class="myapps-sidebar">
        {!isPublicAcct && (
          <div class="filter-section">
            <div class="filter-header">
              <h3>{ownerHeader}</h3>
            </div>

            <calcite-tree
              onCalciteTreeSelect={this._handleMyContentFilter.bind(this)}
            >
              <calcite-tree-item
                id="myApps"
                selected={this.navbarVM.myAppsDataVM.contentLevel === "myApps"}
              >
                {myApps}
              </calcite-tree-item>
              <calcite-tree-item
                id="orgApps"
                selected={this.navbarVM.myAppsDataVM.contentLevel === "orgApps"}
              >
                {myOrgApps}
              </calcite-tree-item>
            </calcite-tree>
          </div>
        )}

        <div class="filter-section">
          <div class="filter-header">
            <h3>{templateHeader}</h3>
            {this._renderClearBtn("template")}
          </div>

          <calcite-tree
            selection-mode="single"
            onCalciteTreeSelect={this._handleTemplateFilter.bind(this)}
          >
            {[
              ...(this?.navbarVM?.appTypeInfoVM?.appTypeInfo != null
                ? this.navbarVM.appTypeInfoVM.appTypeInfo
                : []),
                ...this.navbarVM.appTypeInfoVM.deprecatedAppList.map((appType) => {
                  return {
                    title: this?.appMessages?.deprecatedApps?.[appType],
                    url: appType,
                  };
                }).toArray()
            ]
              ?.filter((templatePortalItem: __esri.PortalItem) => {
                return this.navbarVM.blockBetaApps
                  ? findAppTypeBadge(
                      templatePortalItem.url as EAppTemplateType
                    ) !== "Beta"
                  : true;
              })
              ?.filter((templatePortalItem: __esri.PortalItem) => {
                return !this.navbarVM.appTypeInfoVM.filterAppsOnMyAppsTab.includes(
                  templatePortalItem.url as EAppTemplateType);
              })
              ?.sort((a, b) => {
                const aField = a?.title ?? HIGH_VAL_UNICODE_CHAR;
                const bField = b?.title ?? HIGH_VAL_UNICODE_CHAR;
                return aField?.localeCompare(bField, this._locale);
              })
              ?.map((templatePortalItem: __esri.PortalItem) => {
                const templateValueUrl = Object.values(EAppTemplateType).find(
                  (templateType) => {
                    return templatePortalItem.url.indexOf(templateType) !== -1;
                  }
                );
                const uniqueKey = `${templateValueUrl}-template-filter`;
                return (
                  <calcite-tree-item
                    key={uniqueKey}
                    id={uniqueKey}
                    data-template-type-id={templateValueUrl}
                    selected={
                      this?.navbarVM?.myAppsDataVM?.templatesFilter != null &&
                      this?.navbarVM?.myAppsDataVM?.templatesFilter?.findIndex(
                        (templateFilter) => {
                          return templateFilter === templateValueUrl; // templatePortalItem.url.indexOf(templateType) !== -
                        }
                      ) !== -1
                    }
                  >
                    {templatePortalItem.title}
                  </calcite-tree-item>
                );
              })}
          </calcite-tree>
        </div>

        <div class="filter-section">
          <div class="filter-header">
            <h3>{shareHeader}</h3>
            {this._renderClearBtn("sharing")}
          </div>

          <calcite-tree
            onCalciteTreeSelect={this._handleSharingFilter.bind(this)}
          >
            <calcite-tree-item
              id="owner"
              selected={this.navbarVM.myAppsDataVM.sharingLevel === "owner"}
            >
              {owner}
            </calcite-tree-item>
            {!isPublicAcct && (
              <calcite-tree-item
                id="org"
                selected={this.navbarVM.myAppsDataVM.sharingLevel === "org"}
              >
                {organization}
              </calcite-tree-item>
            )}
            <calcite-tree-item
              id="public"
              selected={this.navbarVM.myAppsDataVM.sharingLevel === "public"}
            >
              {publicLabel}
            </calcite-tree-item>
          </calcite-tree>
        </div>
      </div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------
  private _renderClearBtn(filterType: "owner" | "template" | "sharing"): VNode {
    const { contentLevel, templatesFilter, sharingLevel } =
      this.navbarVM.myAppsDataVM;

    const shouldHide =
      filterType === "owner"
        ? "myApps" === contentLevel
        : filterType === "template"
        ? templatesFilter?.length === 0 || templatesFilter == null
        : filterType === "sharing"
        ? null == sharingLevel
        : null;
    return shouldHide ? null : (
      <calcite-button
        appearance="outline"
        kind="brand"
        icon-start="x"
        onclick={(e) => {
          switch (filterType) {
            case "owner":
              this._handleMyContentFilter(null, "myApps");
              return;
            case "template":
              this._handleTemplateFilter(null, []);
              return;
            case "sharing":
              this._handleSharingFilter(null, null);
              return;
          }
        }}
        title={this?.appMessages?.myApps?.delete?.clear}
      ></calcite-button>
    );
  }

  private _handleMyContentFilter(e: any, override?: any) {
    this.navbarVM.myAppsDataVM.setContentLevelAndRequery(
      override ?? e?.target?.selectedItems?.[0]?.id
    );
  }

  private _handleTemplateFilter(e: any, override?: any) {
    const newTemplatesFilter =
      override ??
      e?.target?.selectedItems?.map(
        (option) => option?.dataset?.templateTypeId
      );
    if (
      this.navbarVM.myAppsDataVM.templatesFilter?.[0] ===
      newTemplatesFilter?.[0]
    ) {
      return;
    }
    this.navbarVM.myAppsDataVM.setTemplatesFilterAndRequery(newTemplatesFilter);
  }

  private _handleSharingFilter(e: any, override?: any) {
    this.navbarVM.myAppsDataVM.setSharingLevelAndRequery(
      override ?? e?.target?.selectedItems?.[0]?.id
    );
  }
}
